import {
  DEVSTACK_DOMAINS,
  DEVSTACK_LABEL,
  DEVSTACK_PREFIXES,
  HOSTS,
  NAME_PREFIXES
} from "./constraints";
import type { Target } from "./types";

/**
 * Reads the devstack out of a hostname.
 *
 * Devstack hostnames are `[<brand>-]devstack-legacy-<branch>.dev-<env>.tvg.com`,
 * so this is two indexOf calls on one label - no pattern needed, and nothing
 * for a crafted hostname to make expensive.
 */
export const readDevstack = (hostname: string) => {
  const label = (hostname || "").split(".")[0].toLowerCase();
  const at = label.indexOf(DEVSTACK_LABEL);

  if (at === -1) {
    return "";
  }

  return NAME_PREFIXES.reduce(
    (name, prefix) =>
      name.startsWith(prefix) ? name.slice(prefix.length) : name,
    label.slice(at + DEVSTACK_LABEL.length)
  );
};

export const hasDevstack = (target: Target) => DEVSTACK_DOMAINS.has(target);

/** Which of the four environments the modal offers this one belongs to. */
export const toTarget = (environment: string): Target => {
  if (environment.startsWith("local")) {
    return "local";
  }

  if (environment === "staging" || environment === "aws-stg") {
    return "staging";
  }

  if (environment === "production" || environment === "aws-prd") {
    return "production";
  }

  return "qa";
};

const PREFIXES = [
  "https://",
  "http://",
  DEVSTACK_LABEL,
  "legacy-",
  "stg-",
  "-"
];

const stripPrefixes = (value: string): string => {
  const prefix = PREFIXES.find((candidate) => value.startsWith(candidate));

  return prefix ? stripPrefixes(value.slice(prefix.length)) : value;
};

/**
 * Reduces whatever was typed to the branch name.
 *
 * Accepts a bare branch, the devstack name, the whole hostname or a pasted
 * URL, because all four are things a developer has to hand. Devstacks only
 * exist for `fb-` branches - .buildkite/development.yaml gates on it - so the
 * prefix goes back on when it was left off.
 */
export const toBranch = (input: string) => {
  // A hostname or a URL: keep the first label.
  const name = stripPrefixes(input.toLowerCase().trim())
    .split("/")[0]
    .split(":")[0]
    .split(".")[0];

  return name && !name.startsWith("fb-") ? `fb-${name}` : name;
};

/**
 * The URL to move to. An empty branch means the environment itself, which is
 * how the input doubles as the way off a devstack.
 */
export const buildUrl = (target: Target, brand: string, branch: string) => {
  const hosts = HOSTS.get(brand) || HOSTS.get("tvg");
  const domain = DEVSTACK_DOMAINS.get(target);

  if (!branch || !domain) {
    return `https://${hosts?.get(target)}`;
  }

  const prefix = DEVSTACK_PREFIXES.get(brand) || "";

  return `https://${prefix}${DEVSTACK_LABEL}legacy-${branch}.${domain}`;
};
